import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { forceLogout } from "../../utlis/forceLogout";

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout();
  return rejectWithValue(error.message);
};

// Create a key for the checklist data based on country and visa category
const createChecklistKey = (country, visaCategory) => {
  return `${country}:${visaCategory}`;
};
// const createChecklistKey = (country) => {
//   return `${country}`;
// };

// Fetch checklist details with caching mechanism
export const fetchChecklistDetails = createAsyncThunk(
  "checklist/fetchChecklistDetails",
  async ({ country, visaCategory, forceRefresh = false }, { getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      // Create a key for this country/visa combination
      const checklistKey = createChecklistKey(country, visaCategory);
      // const checklistKey = createChecklistKey(country);

      // Check if we already have this data in the state
      const { checklists } = getState().clientChecklist;
      if (!forceRefresh && checklists[checklistKey]) {
        // Return the cached data with a flag indicating it was from cache
        return {
          data: checklists[checklistKey],
          key: checklistKey,
          fromCache: true,
        };
      }

      // If not in cache, fetch from API
      const apiUrl = `${
        process.env.REACT_APP_API_BASE_URL
      }/api/checklist/checkListDetails?country=${encodeURIComponent(
        country
      )}&visaCategory=${encodeURIComponent(visaCategory)}`;
      // const apiUrl = `${
      //   process.env.REACT_APP_API_BASE_URL
      // }/api/checklist/checkListDetails?country=${encodeURIComponent(
      //   country
      // )}`;

      const response = await apiFetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response._data ?? await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch checklist details");
      }

      // Return the API data with the key and a flag indicating it was from API
      return {
        data: data.body,
        key: checklistKey,
        fromCache: false,
      };
    } catch (err) {
      return handleAuthError(err, rejectWithValue);
    }
  }
);

// Redux Slice for client checklist
const clientChecklistSlice = createSlice({
  name: "clientChecklist",
  initialState: {
    checklists: {}, // Will store checklist data by key (country:visaCategory)
    isLoading: false,
    error: null,
    currentChecklist: null, // The currently active checklist
  },
  reducers: {
    clearChecklistData: (state) => {
      state.checklists = {};
      state.currentChecklist = null;
    },
    setCurrentChecklist: (state, action) => {
      const { country, visaCategory } = action.payload;
      const key = createChecklistKey(country, visaCategory);

      if (state.checklists[key]) {
        state.currentChecklist = state.checklists[key];
      } else {
        state.currentChecklist = null;
      }
    },
    // Add a new action to clear only the current checklist
    clearCurrentChecklist: (state) => {
      state.currentChecklist = null;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChecklistDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChecklistDetails.fulfilled, (state, action) => {
        state.isLoading = false;

        // Only update the cache if the data came from the API (not from cache)
        if (!action.payload.fromCache) {
          // Store the checklist data in our cache
          state.checklists[action.payload.key] = action.payload.data;
        }

        // Set as current checklist
        state.currentChecklist = action.payload.data;
      })
      .addCase(fetchChecklistDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export selectors for easy access to state
export const selectChecklistByCountryAndCategory = (
  state,
  country,
  visaCategory
) => {
  const key = createChecklistKey(country, visaCategory);
  return state.clientChecklist.checklists[key] || null;
};

export const selectCurrentChecklist = (state) =>
  state.clientChecklist.currentChecklist;
export const selectIsLoading = (state) => state.clientChecklist.isLoading;
export const selectError = (state) => state.clientChecklist.error;

export const {
  clearChecklistData,
  setCurrentChecklist,
  clearCurrentChecklist,
} = clientChecklistSlice.actions;
export default clientChecklistSlice.reducer;
